<template>
  <BaseDetailsPreviewEditHeader
    :title="assortmentName"
    link-name="assortments-management"
    hide-layout-options
    :mode="mode"
    :is-modified="isModified"
    :hide-save-cancel-buttons="!isModified"
    @cancel="onCancelEdit"
    @save="onSaveChanges">
  </BaseDetailsPreviewEditHeader>
  <div class="flex flex-col gap-3 px-2 py-4">
    <div class="font-light">{{ $t('common.defineSetsMustAtLeast') }}</div>
    <div class="flex flex-col p-4 bg-white border rounded border-slate-50">
      <div class="pb-2 font-medium underline underline-offset-8 decoration-2 decoration-theme-600">
        {{ $t('common.materialsGroups') }}
      </div>
      <AssortmentDetailsTable
        v-if="detailsNotInSet"
        table-id="materials-groups-table"
        :headers="assortmentDetailsHeaders"
        :list="detailsNotInSet"
        :mode="mode"
        :hide-multiselect-move-to="detailsSets.length == 0"
        @add-new="onAddItems"
        @delete="onDeleteRow"
        @refresh-data="onRefreshMaterialsGroups"
        @delete-multiselect="onDeleteMultiselect"
        @required-status-change="onRequiredStatusChange"
        @define-set="onDefineSet"
        @move-to="onMoveItems"
        @select-all="onRequiredStatusMassSelection" />
    </div>
    <div class="flex flex-col bg-white border rounded border-slate-50">
      <div
        class="p-4 pb-2 font-medium underline underline-offset-8 decoration-2 decoration-theme-600">
        {{ $t('common.definedSets') }}
      </div>
      <AssortmentDetailsSets
        v-if="detailsSets"
        :mode="mode"
        :sets="detailsSets"
        @edit="onEditSet"
        @delete="onDeleteSet" />
    </div>
  </div>
  <BaseModalConfirm v-if="modalDelete" @cancel="onCloseModalDelete" @confirm="onDeleteSingle">
    <template v-if="editSetId">
      {{ $t('messages.info.deleteSingle') }}
      <span v-if="setItemToRemove">
        <span class="font-medium text-theme-700">{{ setItemToRemove.description }} </span> from
      </span>
      <span class="font-medium text-theme-700">{{ editSetName }} </span>?
    </template>
    <template v-if="deleteRowItem">
      {{ $t('messages.info.deleteSingle') }}
      <span class="font-medium text-theme-700">{{ deleteRowItem.description }} </span>?
    </template>
  </BaseModalConfirm>
  <BaseModalEditCreate
    mode="select"
    v-if="showModal"
    :title="assortmentBindingType == 2 ? 'Materials' : 'Groups'"
    @dismissChanges="
      showModal = false;
      getSelectedRows = false;
    "
    :isModified="isModified"
    @save="onAddMaterialsGroups"
    max-width="extra">
    <template #form>
      <div class="overflow-hidden h-full grid grid-rows-[auto_1fr]">
        <div class="px-2 font-light">{{ $t('messages.info.ctrlClickToAddItems') }}</div>
        <div class="w-full h-full overflow-x-hidden">
          <BaseTable
            v-if="materialsGroups"
            class="overflow-y-auto"
            :headers="assortmentBindingType == 2 ? materialsHeaders : groupHeaders"
            :list="materialsGroups"
            :total-records="materialsGroupsTotalCount"
            is-add-new-hidden
            is-download-button-hidden
            :emit-selected-rows="getSelectedRows"
            hide-multiselect-delete
            hide-multiselect-download
            table-id="add-materials-groups"
            @refresh-data="onRefreshData"
            @selected-rows="onSaveSelectedRows">
          </BaseTable>
        </div>
      </div>
    </template>
  </BaseModalEditCreate>
  <AssortmentDetailsSetsModalMoveItems
    v-if="moveItemsToSetModalActive"
    :sets="setNames"
    :items="setItems"
    @move="onMoveToSet"
    @close-modal="moveItemsToSetModalActive = false"></AssortmentDetailsSetsModalMoveItems>
  <AssortmentDetailsSetsModalCreateEdit
    v-if="createEditSetModalActive"
    :assortment-set="assortmentSet"
    :mode="createEditModalMode"
    @save="onSaveSet"
    @close-modal="
      {
        createEditSetModalActive = false;
        assortmentSet = undefined;
      }
    "></AssortmentDetailsSetsModalCreateEdit>
  <BaseDrawerConfirm
    :is-active="confirmModalActive"
    @cancel="confirmModalActive = false"
    @confirm="onDiscardChanges()" />
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue';
import {
  getAssortmentsDetails,
  getAssortmentById,
  removeAssortmentDetailsByIds,
  getMaterialsWithConditions,
  addOrEditAssortmentDetails,
} from '@/services/api/materials';
import { getGroupItemsWithMaterials } from '@/services/api/groups';
import { useRoute } from 'vue-router';
import { useToastStore } from '@/stores/toast';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import AssortmentDetailsTable from '@/components/assortmentsManagement/AssortmentDetailsTable.vue';
import AssortmentDetailsSets from '@/components/assortmentsManagement/AssortmentDetailsSets.vue';
import AssortmentDetailsSetsModalCreateEdit from '@/components/assortmentsManagement/AssortmentDetailsSetsModalCreateEdit.vue';
import AssortmentDetailsSetsModalMoveItems from '@/components/assortmentsManagement/AssortmentDetailsSetsModalMoveItems.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';
import type { AssortmentDetails, Material, AssortmentSet } from '@/types/Materials';
import type { GroupItemWithAncestry } from '@/types/Groups';
import type { TableFilterData } from '@/types/Base';
import { v4 as uuidv4 } from 'uuid';
import { filterData } from '@/utils/table';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });

const route = useRoute();
const toastStore = useToastStore();
const details = ref<AssortmentDetails[]>([]);

const assortmentDetailsHeaders = [
  { value: 'mustHave', text: $t('common.must'), type: 'check', active: true },
  { value: 'description', text: $t('document-type.description'), type: 'text', active: true },
];
const assortmentName = ref<string>('');

const mode = ref<'edit' | 'view'>('edit');

const assortmentBindingType = ref();

const originalData = ref<AssortmentDetails[]>([]);

async function populateGroupsMaterials(bindingType: number) {
  let resp;
  if (bindingType == 2) {
    resp = await getMaterialsWithConditions({
      itemsPerPage: 50,
      includeDeleted: false,
      excludeIDs: details.value.map((x) => x.bindingID),
    });
  } else {
    resp = await getGroupItemsWithMaterials({
      itemsPerPage: 50,
      excludeIDs: details.value.map((x) => x.bindingID),
    });
  }
  if (resp) {
    materialsGroups.value = resp.data;
    materialsGroupsTotalCount.value = resp.count;
  }
}

onMounted(async () => {
  if (route.params.id) {
    originalData.value = await getAssortmentsDetails(route.params.id as string);
    details.value = await getAssortmentsDetails(route.params.id as string);

    const assortment = await getAssortmentById(route.params.id as string);
    if (assortment) {
      assortmentBindingType.value = assortment.bindingType;
      assortmentName.value = assortment.traderGroupItemNamePath;
      await populateGroupsMaterials(assortment.bindingType);
    }
  }
});

function onRequiredStatusChange(id: string) {
  const detail = details.value.find((x: AssortmentDetails) => x.id == id);
  if (detail) detail.mustHave = !detail.mustHave;
}

function onRequiredStatusMassSelection(value: boolean) {
  details.value.forEach((item: AssortmentDetails) => {
    item.mustHave = value;
  });
}

const detailsNotInSet = computed(() => {
  let temp = details.value.filter((x) => x.similarID == null && x.minQuantity == null);
  if (materialGroupsFilterData.value && materialGroupsFilterData.value.query) {
    temp = filterData(temp, materialGroupsFilterData.value.query, ['description']);
  }
  return temp;
});

const detailsSets = computed(() => {
  return details.value.filter((x) => x.minQuantity);
});
const idsToDelete = ref<string[]>([]);

async function onDeleteMultiselect(items: AssortmentDetails[]) {
  idsToDelete.value.push(...items.filter((item) => item.isFromManager).map((item) => item.id));
  if (details.value)
    details.value = details.value.filter((type) => !idsToDelete.value.includes(type.id));
}
const modalDelete = ref(false);
const deleteRowItem = ref<AssortmentDetails>();

function onDeleteRow(item: AssortmentDetails) {
  deleteRowItem.value = item;
  modalDelete.value = true;
}

async function onDeleteSingle() {
  modalDelete.value = false;
  if (editSetId.value && editSetItems.value) {
    await deleteSet();
    return;
  }
  if (!deleteRowItem.value) return;
  idsToDelete.value.push(deleteRowItem.value.id);
  if (details.value)
    details.value = details.value.filter((type) => type.id !== deleteRowItem.value?.id);
}

const showModal = ref(false);

function onAddItems() {
  getSelectedRows.value = false;
  showModal.value = true;
}

const materialGroupsFilterData = ref<TableFilterData>({ query: '' });

async function onRefreshMaterialsGroups(data: TableFilterData) {
  if (mode.value == 'view') {
    details.value = await getAssortmentsDetails(route.params.id as string);
    if (data.query) details.value = filterData(details.value, data.query, ['description']);
    return;
  }
  materialGroupsFilterData.value = data;
}

const materialsHeaders = [
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
];

const groupHeaders = [{ value: 'ancestry', text: 'Name', type: 'text', active: true }];

const materialsGroups = ref<Material[] | GroupItemWithAncestry[]>([]);
const materialsGroupsTotalCount = ref<number>();

async function onRefreshData(data: TableFilterData) {
  let sortColumn = 'name';
  if (assortmentBindingType.value == 2)
    sortColumn = data.column !== undefined ? materialsHeaders[data.column]?.value : 'name';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    excludeIDs: details.value.map((x) => x.bindingID),
  };

  const res =
    assortmentBindingType.value == 2
      ? await getMaterialsWithConditions(params)
      : await getGroupItemsWithMaterials(params);
  if (res) {
    materialsGroups.value = res.data;
    materialsGroupsTotalCount.value = res.count;
  }
}
const getSelectedRows = ref(false);

function onAddMaterialsGroups() {
  getSelectedRows.value = true;
}

async function onSaveSelectedRows(rows: Material[] | GroupItemWithAncestry[]) {
  showModal.value = false;
  for (const row of rows) {
    details.value.push({
      id: uuidv4(),
      isActive: true,
      isFromManager: true,
      mustHave: false,
      bindingID: row.id,
      assortmentID: route.params.id as string,
      description: assortmentBindingType.value == 1 && 'ancestry' in row ? row.ancestry : row.name,
    });
  }
  await populateGroupsMaterials(assortmentBindingType.value);
}

async function onSaveChanges() {
  for (const detail of details.value) {
    await addOrEditAssortmentDetails(detail);
  }
  if (idsToDelete.value) await removeAssortmentDetailsByIds(idsToDelete.value);
  await populateGroupsMaterials(assortmentBindingType.value);
  toastStore.success({ text: `${$t('common.toast.success')}` });
  isModified.value = false;
}

//SETS

const setNames = computed(() => {
  return detailsSets.value
    .filter((item, index, self) => index === self.findIndex((t) => t.similarID === item.similarID))
    .map((item: any) => ({
      name: `Set: Same as ${item.description}`,
      id: item.similarID,
      minQuantity: item.minQuantity,
    }));
});

const setItems = ref<AssortmentDetails[]>([]);
const createEditSetModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

const assortmentSet = ref<AssortmentSet>();

async function onDefineSet(items: AssortmentDetails[]) {
  setItems.value = items;

  assortmentSet.value = {
    id: uuidv4(),
    name: `Same as ${items[0].description}`,
    minQuantity: undefined,
    items: setItems.value,
  };
  createEditSetModalActive.value = true;
}

async function onSaveSet(setItems: AssortmentDetails[]) {
  const detailMap = new Map(details.value.map((detail, index) => [detail.id, index]));

  setItems.forEach((setItem) => {
    const matchingDetailIndex = detailMap.get(setItem.id);
    if (matchingDetailIndex !== undefined) {
      details.value[matchingDetailIndex] = setItem;
    }
  });

  assortmentSet.value = undefined;
  createEditSetModalActive.value = false;
  setItemToRemove.value = undefined;
  editSetId.value = undefined;
  editSetName.value = undefined;
  editSetItems.value = [];
}

const editSetId = ref();
const editSetName = ref();
const setItemToRemove = ref();
const editSetItems = ref<AssortmentDetails[]>([]);

async function onEditSet(set: { items: AssortmentDetails[]; name: string }) {
  if (!set.items[0].similarID) return;

  assortmentSet.value = {
    id: set.items[0]?.similarID,
    name: set.name,
    minQuantity: set.items[0].minQuantity || undefined,
    items: set.items,
  };
  createEditSetModalActive.value = true;
}

async function onDeleteSet(set: {
  items: AssortmentDetails[];
  name: string;
  removeIndex?: number;
}) {
  editSetId.value = set.items[0].similarID;
  editSetName.value = set.name;
  editSetItems.value = set.items;
  modalDelete.value = true;
  if (set.removeIndex != undefined) {
    setItemToRemove.value = set.items[set.removeIndex];
  }
}

async function onRemoveSetItem() {
  let remainingSetItems: AssortmentDetails[] = [];
  if (setItemToRemove.value.minQuantity == editSetItems.value.length) {
    remainingSetItems = editSetItems.value
      .filter((item) => item !== setItemToRemove.value)
      .map((item) => ({
        ...item,
        minQuantity: item.minQuantity ? item.minQuantity - 1 : item.minQuantity,
      }));
  }

  await onSaveSet([
    ...remainingSetItems,
    {
      ...setItemToRemove.value,
      similarID: null,
      minQuantity: null,
    },
  ]);
}

async function deleteSet() {
  if (setItemToRemove.value) {
    await onRemoveSetItem();
    return;
  }
  editSetItems.value.forEach((item) => {
    item.minQuantity = null;
    item.similarID = null;
  });

  await onSaveSet(editSetItems.value);
}

const moveItemsToSetModalActive = ref(false);

function onMoveItems(items: AssortmentDetails[]) {
  setItems.value = items;
  moveItemsToSetModalActive.value = true;
}

async function onMoveToSet(set: { id: string; minQuantity: number }) {
  moveItemsToSetModalActive.value = false;
  setItems.value = setItems.value.map((item) => ({
    ...item,
    minQuantity: set.minQuantity,
    similarID: set.id,
  }));
  await onSaveSet(setItems.value);
}

function onCloseModalDelete() {
  modalDelete.value = false;
  editSetId.value = undefined;
  editSetName.value = undefined;
  deleteRowItem.value = undefined;
  setItemToRemove.value = undefined;
  editSetItems.value = [];
}
const confirmModalActive = ref(false);

async function onCancelEdit() {
  if (isModified.value) confirmModalActive.value = true;
}

const isModified = ref<boolean>(false);

async function onDiscardChanges() {
  confirmModalActive.value = false;
  details.value = await getAssortmentsDetails(route.params.id as string);
  await populateGroupsMaterials(assortmentBindingType.value);
}

watch(
  () => details.value,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(originalData.value);
  },
  { deep: true }
);
</script>
<style>
form {
  @apply !space-y-0;
}
</style>
